import React, { useEffect, useState } from "react";
import { db, auth } from "../firebaseConfig";
import {
  collection, addDoc, Timestamp, onSnapshot, query, orderBy,
} from "firebase/firestore";
import styled, { keyframes, createGlobalStyle, css } from "styled-components";
import { enqueueSnackbar } from "notistack";

// ─── Constants ────────────────────────────────────────────────────────────────

const CURRENCIES = [
  { value: "GBP", label: "GBP £", symbol: "£", color: "#2563eb", bg: "#eff6ff", flag: "🇬🇧" },
  { value: "PKR", label: "PKR ₨", symbol: "₨", color: "#059669", bg: "#ecfdf5", flag: "🇵🇰" },
  { value: "USD", label: "USD $",  symbol: "$",  color: "#7c3aed", bg: "#f5f3ff", flag: "🇺🇸" },
];

const BANKS = [

  "ALLIED",
  "NATWEST",
  "MONZO",
  "STARLING",
  "TIDE",
  "AMERICAN",
];

const getDisplayName = (email) => {
  if (!email) return "User";
  return email.split("@")[0].replace(/\./g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const EMPTY_FORM = {
  folderNo: "", currency: "GBP", amount: "", rate: "",
  reference: "", date: "", remarks: "", bankName: "",
};



// ─── Global ───────────────────────────────────────────────────────────────────

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');
`;

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-12px); }
  to   { opacity: 1; transform: translateX(0); }
`;
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.5; }
`;

// ─── Styled Components ────────────────────────────────────────────────────────

const Page = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  font-family: 'DM Sans', sans-serif;
  padding: 40px 24px 80px;
`;
const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;
const Header = styled.div`
  margin-bottom: 40px;
  animation: ${fadeUp} 0.4s ease both;
`;
const PageLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 6px;
`;
const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 4px;
  letter-spacing: -0.5px;
`;
const PageSub = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
`;
const Divider = styled.div`
  height: 1px;
  background: #e2e8f0;
  margin: 32px 0;
`;

/* ── Currency picker ── */

const CurrencyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  margin-bottom: 28px;
  animation: ${fadeUp} 0.35s ease 0.05s both;
`;
const CurrencyCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 18px 20px;
  background: ${({ $active, $bg }) => $active ? $bg : "white"};
  border: 2px solid ${({ $active, $color }) => $active ? $color : "#e2e8f0"};
  border-radius: 14px;
  cursor: pointer;
  text-align: left;
  transition: all 0.18s ease;
  box-shadow: ${({ $active, $color }) => $active ? `0 4px 20px ${$color}22` : "none"};

  &:hover {
    border-color: ${({ $color }) => $color};
    transform: translateY(-1px);
    box-shadow: 0 4px 16px ${({ $color }) => $color}18;
  }
  .flag     { font-size: 22px; }
  .cur-code {
    font-size: 16px; font-weight: 700;
    color: ${({ $active, $color }) => $active ? $color : "#0f172a"};
    font-family: 'DM Mono', monospace;
  }
  .cur-label { font-size: 11px; color: #94a3b8; font-weight: 500; }
`;

/* ── Form card ── */

const FormCard = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 16px;
  animation: ${slideIn} 0.25s ease both;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: ${({ $color }) => $color || "#2563eb"};
    border-radius: 16px 16px 0 0;
  }
`;
const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  gap: 8px;

  &::after { content: ''; flex: 1; height: 1px; background: #f1f5f9; }
`;
const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ $cols }) => $cols || "repeat(2, 1fr)"};
  gap: 14px;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;
const FieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  grid-column: ${({ $span }) => $span ? "1 / -1" : "auto"};
`;
const FieldLabel = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.6px;
`;
const RequiredDot = styled.span`
  display: inline-block;
  width: 4px; height: 4px;
  border-radius: 50%;
  background: #ef4444;
  margin-left: 3px;
  vertical-align: middle;
`;
const Input = styled.input`
  padding: 10px 13px;
  font-size: 13px;
  font-family: 'DM Sans', sans-serif;
  border: 1.5px solid #e2e8f0;
  border-radius: 9px;
  outline: none;
  background: #f8fafc;
  color: #0f172a;
  transition: all 0.15s;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    border-color: ${({ $color }) => $color || "#2563eb"};
    background: white;
    box-shadow: 0 0 0 3px ${({ $color }) => $color || "#2563eb"}15;
  }
  &::placeholder { color: #cbd5e1; }
  &:read-only { opacity: 0.7; cursor: default; }
`;
const StyledSelect = styled.select`
  padding: 10px 13px;
  font-size: 13px;
  font-family: 'DM Sans', sans-serif;
  border: 1.5px solid #e2e8f0;
  border-radius: 9px;
  outline: none;
  background: #f8fafc;
  color: #0f172a;
  cursor: pointer;
  width: 100%;
  box-sizing: border-box;
  transition: all 0.15s;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 32px;

  &:focus {
    border-color: ${({ $color }) => $color || "#2563eb"};
    background-color: white;
    box-shadow: 0 0 0 3px ${({ $color }) => $color || "#2563eb"}15;
  }
`;
const Textarea = styled.textarea`
  padding: 10px 13px;
  font-size: 13px;
  font-family: 'DM Sans', sans-serif;
  border: 1.5px solid #e2e8f0;
  border-radius: 9px;
  outline: none;
  background: #f8fafc;
  color: #0f172a;
  transition: all 0.15s;
  width: 100%;
  box-sizing: border-box;
  resize: vertical;
  min-height: 90px;

  &:focus {
    border-color: ${({ $color }) => $color || "#2563eb"};
    background: white;
    box-shadow: 0 0 0 3px ${({ $color }) => $color || "#2563eb"}15;
  }
  &::placeholder { color: #cbd5e1; }
`;

/* ── GBP preview ── */

const GBPPreview = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: linear-gradient(135deg, #eff6ff, #f0fdf4);
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  margin-top: 16px;

  .label  { font-size: 12px; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
  .amount { font-size: 20px; font-weight: 700; color: #1d4ed8; font-family: 'DM Mono', monospace; }
  .eq     { font-size: 11px; color: #94a3b8; margin-left: auto; }
`;

/* ── Submit ── */

const SubmitBtn = styled.button`
  width: 100%;
  padding: 14px;
  font-size: 14px;
  font-weight: 600;
  font-family: 'DM Sans', sans-serif;
  color: white;
  background: ${({ $color }) => $color || "#2563eb"};
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.15s;
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    filter: brightness(1.07);
    transform: translateY(-1px);
    box-shadow: 0 6px 20px ${({ $color }) => $color || "#2563eb"}44;
  }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

/* ── Table ── */

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  h3    { font-size: 16px; font-weight: 600; color: #0f172a; margin: 0; }
  .count {
    font-size: 12px; font-weight: 500; color: #64748b;
    background: #f1f5f9; padding: 3px 10px; border-radius: 20px;
  }
`;
const TableWrap = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;
  animation: ${fadeUp} 0.4s ease 0.1s both;
`;
const TableHead = styled.div`
  display: grid;
  grid-template-columns: 90px 90px 110px 100px 110px 100px 100px;
  padding: 10px 16px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  gap: 12px;

  span {
    font-size: 10px; font-weight: 600; color: #94a3b8;
    text-transform: uppercase; letter-spacing: 0.7px;
  }
`;
const TableRow = styled.div`
  display: grid;
  grid-template-columns: 90px 90px 110px 100px 110px 100px 100px;
  padding: 13px 16px;
  border-bottom: 0.5px solid #f1f5f9;
  gap: 12px;
  align-items: center;
  transition: background 0.1s;
  animation: ${fadeUp} 0.3s ease both;
  animation-delay: ${({ $i }) => Math.min($i * 30, 200)}ms;

  &:last-child  { border-bottom: none; }
  &:hover       { background: #fafbfc; }

  .cell  { font-size: 13px; color: #334155; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .mono  { font-family: 'DM Mono', monospace; font-size: 12px; color: #475569; }
`;
const CurrencyPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  font-family: 'DM Mono', monospace;
`;
const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $s }) =>
    $s === "CONFIRMED" ? "#ecfdf5" :
    $s === "REJECTED"  ? "#fef2f2" : "#fffbeb"};
  color: ${({ $s }) =>
    $s === "CONFIRMED" ? "#065f46" :
    $s === "REJECTED"  ? "#991b1b" : "#92400e"};

  &::before {
    content: '';
    width: 5px; height: 5px;
    border-radius: 50%;
    background: ${({ $s }) =>
      $s === "CONFIRMED" ? "#10b981" :
      $s === "REJECTED"  ? "#ef4444" : "#f59e0b"};
    ${({ $s }) => $s === "PENDING" && css`animation: ${pulse} 1.4s ease infinite;`}
  }
`;
const EmptyState = styled.div`
  padding: 52px 24px;
  text-align: center;
  color: #94a3b8;

  .icon { font-size: 36px; margin-bottom: 10px; opacity: 0.45; }
  .msg  { font-size: 14px; color: #64748b; font-weight: 500; }
  .sub  { font-size: 12px; margin-top: 4px; }
`;

// ─── Component ────────────────────────────────────────────────────────────────

export default function PaymentRequestPage() {
  const [form, setForm]           = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [myRequests, setMyRequests] = useState([]);
  const [user, setUser]           = useState(null);

  useEffect(() => { setUser(auth.currentUser); }, []);

  // Listen to this agent's own requests from PaymentConfirmationRequests
  useEffect(() => {
    if (!user?.email) return;
    const agentName = getDisplayName(user.email);
    const q = query(collection(db, "PaymentConfirmationRequests"), orderBy("timestamp", "desc"));
    return onSnapshot(q, (snap) => {
      const all  = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const mine = all.filter((r) => r.agent === agentName);
      setMyRequests(mine);
    });
  }, [user?.email]);

  const [gbpToPkr, setGbpToPkr] = useState(null);
  const [gbpToUsd, setGbpToUsd] = useState(null);

useEffect(() => {
  const getRates = async () => {
    try {
      const res = await fetch(
        "https://open.er-api.com/v6/latest/GBP"
      );

      const data = await res.json();

      console.log("API RESPONSE:", data);

      setGbpToPkr(data.rates.PKR);
      setGbpToUsd(data.rates.USD);
    } catch (err) {
      console.log("Error fetching rates:", err);
    }
  };

  getRates();
}, []);


const convertToGBP = (amount, currency, rate) => {
  const amt = parseFloat(amount) || 0;
  const r   = parseFloat(rate)   || 1;
  if (currency === "GBP") return amt;
  if (currency === "PKR") return amt/ gbpToPkr;
  if (currency === "USD") return amt/ gbpToUsd;
  return amt / r;
};


useEffect(() => {
  if (gbpToPkr || gbpToUsd) {
    handleChange(
      "rate",
      form.currency === "PKR" ? gbpToPkr : gbpToUsd
    );
  }
}, [gbpToPkr, gbpToUsd]);


  const activeCurrency  = CURRENCIES.find((c) => c.value === form.currency) || CURRENCIES[0];
  const gbpEquivalent   = convertToGBP(form.amount, form.currency, form.rate);

  const handleChange = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const validate = () => {
    if (!form.folderNo || !form.amount || !form.bankName || !form.date) return false;
    if ((form.currency === "PKR" || form.currency === "USD") && !form.rate) return false;
    return true;
  };

  const handleSubmit = async () => {
    if (!validate())
      return enqueueSnackbar("Please fill all required fields", { variant: "error" });

    const amt        = parseFloat(form.amount) || 0;
    const rate       = parseFloat(form.rate)   || 1;
    const paymentGBP = form.currency === "GBP" ? amt : parseFloat((amt / rate).toFixed(2));
    const paymentPKR = form.currency === "PKR" ? amt : null;
    const paymentUSD = form.currency === "USD" ? amt : null;


    console.log(paymentGBP, paymentPKR, paymentUSD);
    console.log(rate);
    console.log("ABOVE ABOVE")
  console.log((form.currency !== "GBP") ? rate  : null);
    setSubmitting(true);
    try {
      await addDoc(collection(db, "PaymentConfirmationRequests"), {
        folderNo:   form.folderNo,
        agent:      getDisplayName(user?.email),
        paymentGBP,
        paymentPKR,
        paymentUSD,
        currency:   form.currency,
        rate:       (form.currency !== "GBP") ? rate : null,
        reference:  form.reference || "-",
        date:       form.date      || "-",
        remarks:    form.remarks   || "-",
        bankName:   form.bankName,
        status:     "PENDING",
        timestamp:  Timestamp.now(),
      });
      enqueueSnackbar("Payment request submitted ✓", { variant: "success" });
      setForm(EMPTY_FORM);
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Failed to submit request", { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      <GlobalStyle />
      <Page>
        <Inner>

          <Header>
            <PageLabel>Finance · Agent Portal</PageLabel>
            <PageTitle>Payment Requests</PageTitle>
            <PageSub>Submit a bank payment for admin confirmation</PageSub>
          </Header>

          {/* ── Currency selector ── */}
          <SectionTitle><h3>Select currency</h3></SectionTitle>
          <CurrencyGrid>
            {CURRENCIES.map((cur) => (
              <CurrencyCard
                key={cur.value}
                $active={form.currency === cur.value}
                $color={cur.color} $bg={cur.bg}
                onClick={() => handleChange("currency", cur.value)}
              >
                <div className="flag">{cur.flag}</div>
                <div className="cur-code">{cur.value}</div>
                <div className="cur-label">{cur.label}</div>
              </CurrencyCard>
            ))}
          </CurrencyGrid>

          {/* ── Form ── */}
          <FormCard $color={activeCurrency.color}>
            <SectionLabel>Payment Details</SectionLabel>
            <FieldGrid $cols="repeat(3, 1fr)">

              <FieldWrap>
                <FieldLabel>Folder No <RequiredDot /></FieldLabel>
                <Input
                  placeholder="e.g. 1042"
                  value={form.folderNo}
                  onChange={(e) => handleChange("folderNo", e.target.value)}
                  $color={activeCurrency.color}
                />
              </FieldWrap>

              <FieldWrap>
                <FieldLabel>Agent</FieldLabel>
                <Input value={getDisplayName(user?.email)} readOnly $color={activeCurrency.color} />
              </FieldWrap>

              <FieldWrap>
                <FieldLabel>Payment Date <RequiredDot /></FieldLabel>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => handleChange("date", e.target.value)}
                  $color={activeCurrency.color}
                />
              </FieldWrap>

              <FieldWrap>
                <FieldLabel>Amount ({activeCurrency.symbol}) <RequiredDot /></FieldLabel>
                <Input
                  type="number" placeholder="0.00"
                  value={form.amount}
                  onChange={(e) => handleChange("amount", e.target.value)}
                  $color={activeCurrency.color}
                />
              </FieldWrap>

              {(form.currency === "PKR" || form.currency === "USD") && (
                <FieldWrap>
                  <FieldLabel>
                    Rate to GBP <RequiredDot />
                  </FieldLabel>
                  <Input
                    type="number"
                    placeholder={form.currency === "PKR" ? "e.g. 350" : "e.g. 1.27"}
                    value={form.currency === "PKR" ? gbpToPkr : gbpToUsd}
                    readOnly
                    $color={activeCurrency.color}
                  />
                </FieldWrap>
              )}

              <FieldWrap>
                <FieldLabel>Bank Name <RequiredDot /></FieldLabel>
                <StyledSelect
                  value={form.bankName}
                  onChange={(e) => handleChange("bankName", e.target.value)}
                  $color={activeCurrency.color}
                >
                  <option value="">Select bank</option>
                  {BANKS.map((b) => <option key={b} value={b}>{b}</option>)}
                </StyledSelect>
              </FieldWrap>

              <FieldWrap>
                <FieldLabel>Reference / Receipt No</FieldLabel>
                <Input
                  placeholder="TXN-XXXX or receipt no."
                  value={form.reference}
                  onChange={(e) => handleChange("reference", e.target.value)}
                  $color={activeCurrency.color}
                />
              </FieldWrap>

              <FieldWrap $span>
                <FieldLabel>Remarks</FieldLabel>
                <Textarea
                  placeholder="Any additional notes for the admin…"
                  value={form.remarks}
                  onChange={(e) => handleChange("remarks", e.target.value)}
                  $color={activeCurrency.color}
                />
              </FieldWrap>

            </FieldGrid>

            {/* GBP live preview */}
            {form.amount && (
              <GBPPreview>
                <span className="label">GBP Equivalent</span>
                <span className="amount">£{gbpEquivalent.toFixed(2)}</span>
                {form.currency !== "GBP" && (
                  <span className="eq">
                    {activeCurrency.symbol}{parseFloat(form.amount || 0).toLocaleString()}
                    {" "}÷ {form.rate || "?"} rate
                  </span>
                )}
              </GBPPreview>
            )}
          </FormCard>

          <SubmitBtn
            $color={activeCurrency.color}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Submitting…" : "💸 Submit Payment Request"}
          </SubmitBtn>

          <Divider />

          {/* ── My requests history ── */}
          <SectionTitle>
            <h3>My Payment Requests</h3>
            <span className="count">{myRequests.length} total</span>
          </SectionTitle>

          <TableWrap>
            <TableHead>
              <span>Folder</span>
              <span>Date</span>
              <span>Bank</span>
              <span>Currency</span>
              <span>Amount</span>
              <span>GBP</span>
              <span>Status</span>
            </TableHead>

            {myRequests.length === 0 && (
              <EmptyState>
                <div className="icon">💳</div>
                <div className="msg">No payment requests yet</div>
                <div className="sub">Submit your first request above</div>
              </EmptyState>
            )}

            {myRequests.map((req, i) => {
              const cur    = CURRENCIES.find((c) => c.value === req.currency) || CURRENCIES[0];
              const rawAmt = req.currency === "PKR" ? req.paymentPKR
                           : req.currency === "USD" ? req.paymentUSD
                           : req.paymentGBP;
              return (
                <TableRow key={req.id} $i={i}>
                  <div className="cell mono">#{req.folderNo}</div>
                  <div className="cell">{req.date}</div>
                  <div className="cell">{req.bankName}</div>
                  <div className="cell">
                    <CurrencyPill $color={cur.color} $bg={cur.bg}>
                      {cur.flag} {req.currency}
                    </CurrencyPill>
                  </div>
                  <div className="cell mono">
                    {cur.symbol}{(parseFloat(rawAmt) || 0).toLocaleString()}
                  </div>
                  <div className="cell mono">£{(parseFloat(req.paymentGBP) || 0).toFixed(2)}</div>
                  <div className="cell">
                    <StatusPill $s={req.status || "PENDING"}>{req.status || "PENDING"}</StatusPill>
                  </div>
                </TableRow>
              );
            })}
          </TableWrap>

        </Inner>
      </Page>
    </>
  );
}